/**
 * shadcn/ui Colour Scheme Definitions
 *
 * 8 pre-built schemes from shadcn/ui themes.
 * Values are HSL format (H S% L%), applied via JS as hsl() overrides.
 * "default" uses the CSS oklch values (no JS override needed): Crosbe blue
 * primary on neutral surfaces (#3732).
 *
 * @see https://ui.shadcn.com/themes
 */

export type ColorScheme =
  | "default"
  | "blue"
  | "green"
  | "orange"
  | "red"
  | "rose"
  | "violet"
  | "yellow";

export const COLOR_SCHEMES: {
  value: ColorScheme;
  label: string;
  description: string;
  preview: { primary: string; accent: string; background: string };
}[] = [
  {
    // #3732: the default is Crosbe blue (Blue 2, the logo's navy) on neutral
    // surfaces. The value stays "default" so every saved choice keeps working.
    value: "default",
    label: "Crosbe blue",
    description: "Crosbe brand blue",
    preview: { primary: "#003f62", background: "#ffffff", accent: "#f4f4f5" },
  },
  {
    value: "blue",
    label: "Blue",
    description: "Professional blue",
    preview: { primary: "#3b82f6", background: "#ffffff", accent: "#dbeafe" },
  },
  {
    value: "green",
    label: "Green",
    description: "Natural green",
    preview: { primary: "#22c55e", background: "#ffffff", accent: "#dcfce7" },
  },
  {
    value: "orange",
    label: "Orange",
    description: "Warm orange",
    preview: { primary: "#f97316", background: "#ffffff", accent: "#ffedd5" },
  },
  {
    value: "red",
    label: "Red",
    description: "Bold red",
    preview: { primary: "#ef4444", background: "#ffffff", accent: "#fee2e2" },
  },
  {
    value: "rose",
    label: "Rose",
    description: "Elegant rose",
    preview: { primary: "#f43f5e", background: "#ffffff", accent: "#ffe4e6" },
  },
  {
    value: "violet",
    label: "Violet",
    description: "Royal violet",
    preview: { primary: "#8b5cf6", background: "#ffffff", accent: "#ede9fe" },
  },
  {
    value: "yellow",
    label: "Yellow",
    description: "Bright yellow",
    preview: { primary: "#eab308", background: "#ffffff", accent: "#fef9c3" },
  },
];

type ThemeColors = Record<string, string>;

const CSS_VARS = [
  "background",
  "foreground",
  "card",
  "card-foreground",
  "popover",
  "popover-foreground",
  "primary",
  "primary-foreground",
  "secondary",
  "secondary-foreground",
  "muted",
  "muted-foreground",
  "accent",
  "accent-foreground",
  "destructive",
  "destructive-foreground",
  "border",
  "input",
  "ring",
] as const;

/**
 * HSL colour definitions for each scheme (light + dark).
 * "default" is omitted — it uses the CSS oklch values.
 */
const schemes: Record<Exclude<ColorScheme, "default">, { light: ThemeColors; dark: ThemeColors }> = {
  blue: {
    light: {
      background: "0 0% 100%",
      foreground: "222.2 84% 4.9%",
      card: "0 0% 100%",
      "card-foreground": "222.2 84% 4.9%",
      popover: "0 0% 100%",
      "popover-foreground": "222.2 84% 4.9%",
      primary: "221.2 83.2% 53.3%",
      "primary-foreground": "210 40% 98%",
      secondary: "210 40% 96.1%",
      "secondary-foreground": "222.2 47.4% 11.2%",
      muted: "210 40% 96.1%",
      "muted-foreground": "215.4 16.3% 46.9%",
      accent: "210 40% 96.1%",
      "accent-foreground": "222.2 47.4% 11.2%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "210 40% 98%",
      border: "214.3 31.8% 91.4%",
      input: "214.3 31.8% 91.4%",
      ring: "221.2 83.2% 53.3%",
    },
    dark: {
      background: "222.2 84% 4.9%",
      foreground: "210 40% 98%",
      card: "222.2 84% 4.9%",
      "card-foreground": "210 40% 98%",
      popover: "222.2 84% 4.9%",
      "popover-foreground": "210 40% 98%",
      primary: "217.2 91.2% 59.8%",
      "primary-foreground": "222.2 47.4% 11.2%",
      secondary: "217.2 32.6% 17.5%",
      "secondary-foreground": "210 40% 98%",
      muted: "217.2 32.6% 17.5%",
      "muted-foreground": "215 20.2% 65.1%",
      accent: "217.2 32.6% 17.5%",
      "accent-foreground": "210 40% 98%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "210 40% 98%",
      border: "217.2 32.6% 17.5%",
      input: "217.2 32.6% 17.5%",
      ring: "224.3 76.3% 48%",
    },
  },
  green: {
    light: {
      background: "0 0% 100%",
      foreground: "240 10% 3.9%",
      card: "0 0% 100%",
      "card-foreground": "240 10% 3.9%",
      popover: "0 0% 100%",
      "popover-foreground": "240 10% 3.9%",
      primary: "142.1 76.2% 36.3%",
      "primary-foreground": "355.7 100% 97.3%",
      secondary: "240 4.8% 95.9%",
      "secondary-foreground": "240 5.9% 10%",
      muted: "240 4.8% 95.9%",
      "muted-foreground": "240 3.8% 46.1%",
      accent: "240 4.8% 95.9%",
      "accent-foreground": "240 5.9% 10%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "0 0% 98%",
      border: "240 5.9% 90%",
      input: "240 5.9% 90%",
      ring: "142.1 76.2% 36.3%",
    },
    dark: {
      background: "20 14.3% 4.1%",
      foreground: "0 0% 95%",
      card: "24 9.8% 10%",
      "card-foreground": "0 0% 95%",
      popover: "0 0% 9%",
      "popover-foreground": "0 0% 95%",
      primary: "142.1 70.6% 45.3%",
      "primary-foreground": "144.9 80.4% 10%",
      secondary: "240 3.7% 15.9%",
      "secondary-foreground": "0 0% 98%",
      muted: "0 0% 15%",
      "muted-foreground": "240 5% 64.9%",
      accent: "12 6.5% 15.1%",
      "accent-foreground": "0 0% 98%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "0 85.7% 97.3%",
      border: "240 3.7% 15.9%",
      input: "240 3.7% 15.9%",
      ring: "142.4 71.8% 29.2%",
    },
  },
  orange: {
    light: {
      background: "0 0% 100%",
      foreground: "20 14.3% 4.1%",
      card: "0 0% 100%",
      "card-foreground": "20 14.3% 4.1%",
      popover: "0 0% 100%",
      "popover-foreground": "20 14.3% 4.1%",
      primary: "24.6 95% 53.1%",
      "primary-foreground": "60 9.1% 97.8%",
      secondary: "60 4.8% 95.9%",
      "secondary-foreground": "24 9.8% 10%",
      muted: "60 4.8% 95.9%",
      "muted-foreground": "25 5.3% 44.7%",
      accent: "60 4.8% 95.9%",
      "accent-foreground": "24 9.8% 10%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "60 9.1% 97.8%",
      border: "20 5.9% 90%",
      input: "20 5.9% 90%",
      ring: "24.6 95% 53.1%",
    },
    dark: {
      background: "20 14.3% 4.1%",
      foreground: "60 9.1% 97.8%",
      card: "20 14.3% 4.1%",
      "card-foreground": "60 9.1% 97.8%",
      popover: "20 14.3% 4.1%",
      "popover-foreground": "60 9.1% 97.8%",
      primary: "20.5 90.2% 48.2%",
      "primary-foreground": "60 9.1% 97.8%",
      secondary: "12 6.5% 15.1%",
      "secondary-foreground": "60 9.1% 97.8%",
      muted: "12 6.5% 15.1%",
      "muted-foreground": "24 5.4% 63.9%",
      accent: "12 6.5% 15.1%",
      "accent-foreground": "60 9.1% 97.8%",
      destructive: "0 72.2% 50.6%",
      "destructive-foreground": "60 9.1% 97.8%",
      border: "12 6.5% 15.1%",
      input: "12 6.5% 15.1%",
      ring: "20.5 90.2% 48.2%",
    },
  },
  red: {
    light: {
      background: "0 0% 100%",
      foreground: "0 0% 3.9%",
      card: "0 0% 100%",
      "card-foreground": "0 0% 3.9%",
      popover: "0 0% 100%",
      "popover-foreground": "0 0% 3.9%",
      primary: "0 72.2% 50.6%",
      "primary-foreground": "0 85.7% 97.3%",
      secondary: "0 0% 96.1%",
      "secondary-foreground": "0 0% 9%",
      muted: "0 0% 96.1%",
      "muted-foreground": "0 0% 45.1%",
      accent: "0 0% 96.1%",
      "accent-foreground": "0 0% 9%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "0 0% 98%",
      border: "0 0% 89.8%",
      input: "0 0% 89.8%",
      ring: "0 72.2% 50.6%",
    },
    dark: {
      background: "0 0% 3.9%",
      foreground: "0 0% 98%",
      card: "0 0% 3.9%",
      "card-foreground": "0 0% 98%",
      popover: "0 0% 3.9%",
      "popover-foreground": "0 0% 98%",
      primary: "0 72.2% 50.6%",
      "primary-foreground": "0 85.7% 97.3%",
      secondary: "0 0% 14.9%",
      "secondary-foreground": "0 0% 98%",
      muted: "0 0% 14.9%",
      "muted-foreground": "0 0% 63.9%",
      accent: "0 0% 14.9%",
      "accent-foreground": "0 0% 98%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "0 0% 98%",
      border: "0 0% 14.9%",
      input: "0 0% 14.9%",
      ring: "0 72.2% 50.6%",
    },
  },
  rose: {
    light: {
      background: "0 0% 100%",
      foreground: "240 10% 3.9%",
      card: "0 0% 100%",
      "card-foreground": "240 10% 3.9%",
      popover: "0 0% 100%",
      "popover-foreground": "240 10% 3.9%",
      primary: "346.8 77.2% 49.8%",
      "primary-foreground": "355.7 100% 97.3%",
      secondary: "240 4.8% 95.9%",
      "secondary-foreground": "240 5.9% 10%",
      muted: "240 4.8% 95.9%",
      "muted-foreground": "240 3.8% 46.1%",
      accent: "240 4.8% 95.9%",
      "accent-foreground": "240 5.9% 10%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "0 0% 98%",
      border: "240 5.9% 90%",
      input: "240 5.9% 90%",
      ring: "346.8 77.2% 49.8%",
    },
    dark: {
      background: "20 14.3% 4.1%",
      foreground: "0 0% 95%",
      card: "24 9.8% 10%",
      "card-foreground": "0 0% 95%",
      popover: "0 0% 9%",
      "popover-foreground": "0 0% 95%",
      primary: "346.8 77.2% 49.8%",
      "primary-foreground": "355.7 100% 97.3%",
      secondary: "240 3.7% 15.9%",
      "secondary-foreground": "0 0% 98%",
      muted: "0 0% 15%",
      "muted-foreground": "240 5% 64.9%",
      accent: "12 6.5% 15.1%",
      "accent-foreground": "0 0% 98%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "0 85.7% 97.3%",
      border: "240 3.7% 15.9%",
      input: "240 3.7% 15.9%",
      ring: "346.8 77.2% 49.8%",
    },
  },
  violet: {
    light: {
      background: "0 0% 100%",
      foreground: "224 71.4% 4.1%",
      card: "0 0% 100%",
      "card-foreground": "224 71.4% 4.1%",
      popover: "0 0% 100%",
      "popover-foreground": "224 71.4% 4.1%",
      primary: "262.1 83.3% 57.8%",
      "primary-foreground": "210 20% 98%",
      secondary: "220 14.3% 95.9%",
      "secondary-foreground": "220.9 39.3% 11%",
      muted: "220 14.3% 95.9%",
      "muted-foreground": "220 8.9% 46.1%",
      accent: "220 14.3% 95.9%",
      "accent-foreground": "220.9 39.3% 11%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "210 20% 98%",
      border: "220 13% 91%",
      input: "220 13% 91%",
      ring: "262.1 83.3% 57.8%",
    },
    dark: {
      background: "224 71.4% 4.1%",
      foreground: "210 20% 98%",
      card: "224 71.4% 4.1%",
      "card-foreground": "210 20% 98%",
      popover: "224 71.4% 4.1%",
      "popover-foreground": "210 20% 98%",
      primary: "263.4 70% 50.4%",
      "primary-foreground": "210 20% 98%",
      secondary: "215 27.9% 16.9%",
      "secondary-foreground": "210 20% 98%",
      muted: "215 27.9% 16.9%",
      "muted-foreground": "217.9 10.6% 64.9%",
      accent: "215 27.9% 16.9%",
      "accent-foreground": "210 20% 98%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "210 20% 98%",
      border: "215 27.9% 16.9%",
      input: "215 27.9% 16.9%",
      ring: "263.4 70% 50.4%",
    },
  },
  yellow: {
    light: {
      background: "0 0% 100%",
      foreground: "20 14.3% 4.1%",
      card: "0 0% 100%",
      "card-foreground": "20 14.3% 4.1%",
      popover: "0 0% 100%",
      "popover-foreground": "20 14.3% 4.1%",
      primary: "47.9 95.8% 53.1%",
      "primary-foreground": "26 83.3% 14.1%",
      secondary: "60 4.8% 95.9%",
      "secondary-foreground": "24 9.8% 10%",
      muted: "60 4.8% 95.9%",
      "muted-foreground": "25 5.3% 44.7%",
      accent: "60 4.8% 95.9%",
      "accent-foreground": "24 9.8% 10%",
      destructive: "0 84.2% 60.2%",
      "destructive-foreground": "60 9.1% 97.8%",
      border: "20 5.9% 90%",
      input: "20 5.9% 90%",
      ring: "20 14.3% 4.1%",
    },
    dark: {
      background: "20 14.3% 4.1%",
      foreground: "60 9.1% 97.8%",
      card: "20 14.3% 4.1%",
      "card-foreground": "60 9.1% 97.8%",
      popover: "20 14.3% 4.1%",
      "popover-foreground": "60 9.1% 97.8%",
      primary: "47.9 95.8% 53.1%",
      "primary-foreground": "26 83.3% 14.1%",
      secondary: "12 6.5% 15.1%",
      "secondary-foreground": "60 9.1% 97.8%",
      muted: "12 6.5% 15.1%",
      "muted-foreground": "24 5.4% 63.9%",
      accent: "12 6.5% 15.1%",
      "accent-foreground": "60 9.1% 97.8%",
      destructive: "0 62.8% 30.6%",
      "destructive-foreground": "60 9.1% 97.8%",
      border: "12 6.5% 15.1%",
      input: "12 6.5% 15.1%",
      ring: "35.5 91.7% 32.9%",
    },
  },
};

/**
 * Apply a colour scheme to the DOM.
 *
 * For "default", removes all inline style overrides so the CSS oklch values take effect.
 * For other schemes, sets HSL values as inline styles on <html>.
 */
export function applyColorScheme(
  scheme: ColorScheme,
  isDark: boolean
): void {
  const root = document.documentElement;

  if (scheme === "default") {
    // Remove all inline overrides — let CSS handle it
    for (const v of CSS_VARS) {
      root.style.removeProperty(`--${v}`);
    }
    return;
  }

  const colors = schemes[scheme][isDark ? "dark" : "light"];
  for (const [key, value] of Object.entries(colors)) {
    root.style.setProperty(`--${key}`, `hsl(${value})`);
  }
}

/* === App theme registry (#809) =============================================
 *
 * Experimental full re-skins ("themes"), distinct from the light/dark theme and
 * the colour schemes above: a theme is a named boolean flag that scopes a block
 * of CSS onto the app chrome. Every theme is DEFAULT OFF — with no flag stored,
 * the app renders exactly as prod does today (light AND dark). Styling can then
 * ship continuously without changing what users are used to; whoever wants to
 * evaluate a look flips it on in Settings → Appearance → Themes.
 *
 * Registry discipline — adding a future theme means exactly:
 *   1. ONE named flag: add its camelCase key to AppThemeFlag + one entry below.
 *   2. ONE scoped CSS block in app.css under the entry's `className` selector
 *      (nothing outside that selector may change default rendering).
 *   3. ONE registry entry here — the Settings UI renders the toggle from it.
 * Any theme whose CSS touches `position`/layout MUST pass a phone-width run of
 * `.jez/scripts/smoke-770-ios-shell.mjs` with the theme ON as well as OFF (the
 * #770 lesson: an unlayered `position: relative` beat Tailwind's `.fixed` and
 * squashed the app shell on iPhone).
 *
 * Storage v1 (#809): localStorage, same mechanism as the dark-mode toggle above
 * it in ThemeProvider — per-device, zero backend, instant apply/restore. v2
 * (out of scope here) migrates the flags to the per-user ui_prefs backend
 * (#373 / PR #669) when its client sync lands.
 */

/** camelCase flag key per theme — widen this union as themes are added. */
export type AppThemeFlag = "crosbeNavy1" | "crosbeNavy2" | "crosbeBrand1";

/** Enabled-flag map, keyed by AppThemeFlag. Absent key = off (the default). */
export type AppThemeFlags = Partial<Record<AppThemeFlag, boolean>>;

/**
 * Where a theme's class mounts, which is what makes it sidebar-only vs app-wide:
 *  - "sidebar": class applied to the <Sidebar> root — re-skins the rail only
 *    (its CSS targets that element directly, incl. the texture pseudo).
 *  - "root": class applied to <html> — re-maps the global `--color-*` tokens so
 *    EVERY surface (cards, headers, popovers, buttons) picks up the theme for
 *    free. This is how a theme goes "broad" without touching each component.
 */
export type AppThemeScope = "sidebar" | "root";

export interface AppTheme {
  /** Stable kebab-case theme id. */
  id: string;
  /** The named boolean flag (localStorage + code). */
  flag: AppThemeFlag;
  label: string;
  /** One-liner shown under the toggle in Settings. */
  description: string;
  /** The single scoped CSS class this theme applies (see app.css). */
  className: string;
  /** Mount point — see AppThemeScope. Defaults to "sidebar" (back-compat). */
  scope: AppThemeScope;
}

export const APP_THEMES: AppTheme[] = [
  {
    // Akira's branded navy re-skin: the Crosbe-system-login navy (#012f47
    // family), the low-poly texture motif, Manrope, the crosbeAI wordmark.
    // CSS recovered byte-identical from staging (selectors renamed).
    id: "crosbe-navy-1",
    flag: "crosbeNavy1",
    label: "Crosbe navy 1 — sidebar",
    description: "Navy chrome + brand texture on the sidebar",
    className: "sidebar-crosbe-navy-1",
    scope: "sidebar",
  },
  {
    // The navy identity taken app-wide: a root-mounted class that re-maps the
    // core `--color-*` surface tokens to the navy family, so cards, headers,
    // popovers and buttons all adopt it. Composes with crosbe-navy-1 — turn both
    // on for the full navy app (textured navy rail + navy surface).
    id: "crosbe-navy-2",
    flag: "crosbeNavy2",
    label: "Crosbe navy 2 — full surface",
    description: "Navy palette across the whole app (pairs with the sidebar theme)",
    className: "theme-crosbe-navy-2",
    scope: "root",
  },
  {
    // #914-adjacent recovery (15 Jul): Akira's two-tone "vibrant" look — light
    // surface + navy sidebar hero + Crosbe-blue and multi-colour chart accents.
    // Authored on feat/brand-theme for the staging demo but never merged; CSS
    // ported verbatim from that branch (Jez: "akira liked the two tone accents
    // not everything blue").
    id: "crosbe-brand-1",
    flag: "crosbeBrand1",
    label: "Crosbe brand — vibrant",
    description: "Light two-tone: navy sidebar, bright Crosbe-blue + colourful accents",
    className: "theme-crosbe-brand-1",
    scope: "root",
  },
];

/** localStorage key for the theme-flag map (sibling of "crosbe-theme"). */
export const APP_THEMES_STORAGE_KEY = "crosbe-app-themes";

/**
 * Parse the stored flag map. Fail-safe: anything malformed → {} (= every theme
 * off, the default). Only `true` under a known flag key survives, so a stale or
 * hand-edited value can never turn a theme on by accident.
 */
export function parseAppThemeFlags(raw: string | null): AppThemeFlags {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const flags: AppThemeFlags = {};
    for (const theme of APP_THEMES) {
      if ((parsed as Record<string, unknown>)[theme.flag] === true) {
        flags[theme.flag] = true;
      }
    }
    return flags;
  } catch {
    return {};
  }
}

/** Read the flag map from localStorage (SSR/test-safe: no window → all off). */
export function readAppThemeFlags(): AppThemeFlags {
  if (typeof window === "undefined") return {};
  try {
    return parseAppThemeFlags(localStorage.getItem(APP_THEMES_STORAGE_KEY));
  } catch {
    return {};
  }
}

/**
 * The class list the enabled themes apply — "" when everything is off. Pass a
 * `scope` to get only the classes that mount at that level: the Sidebar asks for
 * "sidebar", the root (<html>, via ThemeProvider) asks for "root". Omit to get
 * all enabled classes (back-compat).
 */
export function enabledThemeClasses(
  flags: AppThemeFlags,
  scope?: AppThemeScope
): string {
  return APP_THEMES.filter(
    (t) => flags[t.flag] === true && (scope === undefined || t.scope === scope)
  )
    .map((t) => t.className)
    .join(" ");
}
